/**
 * ToolConfigTab - Configuration panel for agent tools
 *
 * Allows users to configure:
 * - Tool name (how the agent identifies the tool)
 * - Tool description (helps the agent understand when to use the tool)
 * - Target workflow (for handoff / sub-workflow tools)
 * - Agent parameters schema (what info the agent needs to gather)
 */

import { useCallback, useMemo } from 'react';
import {
  makeStyles,
  tokens,
  Field,
  Input,
  Textarea,
  Text,
  InfoLabel,
  Spinner,
} from '@fluentui/react-components';
import { ArrowFlowUpRightRegular } from '@fluentui/react-icons';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { useWorkflows } from '@/hooks/useWorkflows';
import { extractWorkflowName } from '@/types/logicApps';
import { AgentParameterEditor } from './AgentParameterEditor';
import { SearchableCombobox, type SearchableComboboxOption } from '@/components/shared';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    width: '100%',
    minWidth: 0, // Prevent flex overflow
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
  },
  hint: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginTop: tokens.spacingVerticalXXS,
  },
  workflowFieldRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  workflowCombobox: {
    flex: 1,
    minWidth: 0,
  },
  workflowIcon: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  contextInfo: {
    color: tokens.colorNeutralForeground3,
  },
});

interface ToolConfigTabProps {
  /** The tool name (key in the tools object) */
  toolName: string;
  /** The tool description */
  toolDescription?: string;
  /** The agent parameter schema */
  agentParameterSchema?: Record<string, unknown>;
  /** Parent agent name for context */
  parentAgentName?: string;
  /** Target workflow ID (for sub-workflow / handoff tools) */
  targetWorkflowId?: string;
}

export const ToolConfigTab = ({
  toolName: initialToolName,
  toolDescription: initialToolDescription,
  agentParameterSchema: initialAgentParameterSchema,
  parentAgentName,
  targetWorkflowId: initialTargetWorkflowId,
}: ToolConfigTabProps) => {
  const styles = useStyles();
  const setParameter = useNodeEditorStore((state) => state.setParameter);
  const parameterValues = useNodeEditorStore((state) => state.parameterValues);
  
  // Read current values from parameterValues (which track edits), falling back to initial props
  const toolName = (parameterValues['_toolName'] as string | undefined) ?? initialToolName;
  const toolDescription = (parameterValues['_toolDescription'] as string | undefined) ?? initialToolDescription;
  const agentParameterSchema = (parameterValues['_agentParameterSchema'] as Record<string, unknown> | undefined) ?? initialAgentParameterSchema;
  const targetWorkflowId = (parameterValues['_workflowId'] as string | undefined) ?? initialTargetWorkflowId;

  // Detect if this is a sub-workflow / handoff tool
  const isSubworkflowTool = !!targetWorkflowId;

  // Fetch available workflows for the target picker
  const { data: allWorkflows = [], isLoading: isWorkflowsLoading } = useWorkflows();

  // Filter to show all workflows except the current parent workflow
  const availableWorkflows = useMemo(() => {
    return allWorkflows.map((w) => extractWorkflowName(w.name));
  }, [allWorkflows]);

  const workflowOptions = useMemo<SearchableComboboxOption[]>(
    () => availableWorkflows.map((name) => ({ value: name, text: name })),
    [availableWorkflows],
  );

  const handleToolNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setParameter('_toolName', e.target.value);
    },
    [setParameter]
  );

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setParameter('_toolDescription', e.target.value);
    },
    [setParameter]
  );

  const handleTargetWorkflowChange = useCallback(
    (value: string) => {
      if (value) {
        setParameter('_workflowId', value);
      }
    },
    [setParameter]
  );

  const handleSchemaChange = useCallback(
    (schema: Record<string, unknown>) => {
      // Store null if schema has no properties
      const hasProperties = schema.properties && Object.keys(schema.properties as object).length > 0;
      setParameter('_agentParameterSchema', hasProperties ? schema : null);
    },
    [setParameter]
  );

  return (
    <div className={styles.container}>
      {/* Tool Name Section */}
      <div className={styles.section}>
        <Field
          label={
            <InfoLabel
              info="The name the agent uses to identify and call this tool. Use a descriptive name like 'SendEmail' or 'SearchDocuments'."
            >
              Tool Name
            </InfoLabel>
          }
          required
        >
          <Input
            value={toolName}
            onChange={handleToolNameChange}
            placeholder="e.g., SendEmail"
          />
        </Field>
        <Text className={styles.hint}>
          This name is shown to the AI agent when it decides which tool to use.
        </Text>
      </div>

      {/* Tool Description Section */}
      <div className={styles.section}>
        <Field
          label={
            <InfoLabel
              info="A clear description helps the agent understand when and how to use this tool. Be specific about what the tool does."
            >
              Description
            </InfoLabel>
          }
        >
          <Textarea
            value={toolDescription ?? ''}
            onChange={handleDescriptionChange}
            placeholder="Describe what this tool does and when the agent should use it..."
            rows={3}
            resize="vertical"
          />
        </Field>
        <Text className={styles.hint}>
          Good descriptions improve how accurately the agent uses this tool.
        </Text>
      </div>

      {/* Target Workflow Section — only for sub-workflow / handoff tools */}
      {isSubworkflowTool && (
        <div className={styles.section}>
          <Field
            label={
              <InfoLabel
                info="The workflow this tool hands off to. The agent will invoke this workflow when it decides to use this tool."
              >
                Target Workflow
              </InfoLabel>
            }
            required
          >
            <div className={styles.workflowFieldRow}>
              <ArrowFlowUpRightRegular className={styles.workflowIcon} />
              {isWorkflowsLoading ? (
                <Spinner size="tiny" label="Loading workflows..." />
              ) : (
                <SearchableCombobox
                  className={styles.workflowCombobox}
                  options={workflowOptions}
                  value={targetWorkflowId ?? ''}
                  onChange={handleTargetWorkflowChange}
                  placeholder="Select a workflow..."
                  aria-label="Target workflow"
                />
              )}
            </div>
          </Field>
          <Text className={styles.hint}>
            Change which workflow is invoked when the agent uses this handoff tool.
          </Text>
        </div>
      )}

      {/* Agent Parameters Section */}
      <AgentParameterEditor
        schema={agentParameterSchema}
        onChange={handleSchemaChange}
      />

      {/* Context Info */}
      {parentAgentName && (
        <div className={styles.section}>
          <Text size={200} className={styles.contextInfo}>
            This tool belongs to agent: <strong>{parentAgentName}</strong>
          </Text>
        </div>
      )}
    </div>
  );
};

ToolConfigTab.displayName = 'ToolConfigTab';
